import { isAfter, isBefore } from 'date-fns'
import { getIsSeries } from 'libs/utils'
import { Course } from 'types/data'

export enum SeriesStatus {
  PreLaunch = 'preLaunch',
  DuringLaunch = 'duringLaunch',
  PostLaunch = 'postLaunch',
  OffLaunch = 'offLaunch',
}

type StatusSeries = Pick<
  Course,
  | 'type'
  | 'sortedChapters'
  | 'upcomingChapter'
  | 'marketingPostlaunchEndsAt'
  | 'marketingPrelaunchStartsAt'
>

// Pre-launch: period between marketingPrelaunchStartsAt and first episode release date. series is available pre-paywall. no episodes launched.
// During-launch: period between first episode release date and last episode release date.
// Post-launch: period between last episode release date and marketingPostlaunchEndsAt. all episodes launched.
// Off-launch: period before pre-launch and after post-launch.
const matchesConditionsForStatus = (
  series: StatusSeries,
  status: Exclude<SeriesStatus, SeriesStatus.OffLaunch>,
) => {
  const seriesStatusConditions = {
    [SeriesStatus.PreLaunch]: [
      !series.sortedChapters?.length,
      !!series.upcomingChapter,
      !!series.marketingPrelaunchStartsAt,
      isBefore(
        new Date(series.marketingPrelaunchStartsAt as string),
        Date.now(),
      ),
    ],
    [SeriesStatus.DuringLaunch]: [
      !!series.sortedChapters?.length,
      !!series.upcomingChapter,
    ],
    [SeriesStatus.PostLaunch]: [
      !!series.sortedChapters?.length,
      !series.upcomingChapter,
      !!series.marketingPostlaunchEndsAt,
      isAfter(new Date(series.marketingPostlaunchEndsAt as string), Date.now()),
    ],
  }

  return seriesStatusConditions[status].every((condition) => !!condition)
}

export const getSeriesStatus = (series: StatusSeries): SeriesStatus | null => {
  if (!getIsSeries(series.type)) {
    return null
  }

  if (matchesConditionsForStatus(series, SeriesStatus.PreLaunch))
    return SeriesStatus.PreLaunch
  if (matchesConditionsForStatus(series, SeriesStatus.DuringLaunch))
    return SeriesStatus.DuringLaunch
  if (matchesConditionsForStatus(series, SeriesStatus.PostLaunch))
    return SeriesStatus.PostLaunch

  return SeriesStatus.OffLaunch
}

export const isSeriesPreLaunch = (series: StatusSeries) =>
  getSeriesStatus(series) === SeriesStatus.PreLaunch
export const isSeriesDuringLaunch = (series: StatusSeries) =>
  getSeriesStatus(series) === SeriesStatus.DuringLaunch
export const isSeriesPostLaunch = (series: StatusSeries) =>
  getSeriesStatus(series) === SeriesStatus.PostLaunch
export const isSeriesOffLaunch = (series: StatusSeries) =>
  getSeriesStatus(series) === SeriesStatus.OffLaunch
